import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { OrderBlotter } from '../../../components/trading/OrderBlotter';
import type { WidgetInstanceProps } from '../../../components/grid-dashboard';
import { useOrderTicketNavigation } from '../../order-ticket/navigation/OrderTicketNavigationContext';
import { useAuth } from '../../../contexts/AuthContext';
import { tradingService } from '../../../services/TradingService';
import { isActionableOrderBlotterAlert } from './systemAlertPolicy';
import type {
  OrderEvent,
  SystemAlert,
  TradingOrder,
  TradingOrderBlotterRow,
} from '../../../types/trading';

/* ── Helpers (moved from TradingPage) ──────────────────────────────── */

const asOrders = (value: unknown): TradingOrderBlotterRow[] => {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (item): item is TradingOrderBlotterRow => Boolean(item && typeof item === 'object'),
  );
};

const upsertOrder = (
  orders: TradingOrderBlotterRow[],
  incoming: TradingOrderBlotterRow,
): TradingOrderBlotterRow[] => {
  const next = [...orders];
  const index = next.findIndex((o) => o.order_id === incoming.order_id);
  if (index >= 0) {
    next[index] = incoming;
  } else {
    next.unshift(incoming);
  }
  return next.sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at));
};

interface ExtractedOrderEvent {
  order: TradingOrder;
  strategyPositionId: string | null;
}

const extractOrderFromEvent = (event: OrderEvent): ExtractedOrderEvent | null => {
  const payload = event.payload as any;
  if (!payload || typeof payload !== 'object') return null;
  if (payload.order && typeof payload.order === 'object') {
    const strategyPositionId =
      typeof payload.strategy_position_id === 'string' && payload.strategy_position_id.trim().length > 0
        ? payload.strategy_position_id.trim()
        : null;
    return {
      order: payload.order as TradingOrder,
      strategyPositionId,
    };
  }
  return null;
};

const asBlotterStatus = (status: TradingOrder['status']): TradingOrderBlotterRow['status'] => {
  if (status === 'pending') return 'pending';
  if (status === 'filled') return 'filled';
  return 'cancelled';
};

const asBlotterOrder = (
  order: TradingOrder,
  strategyPositionId: string | null = null,
): TradingOrderBlotterRow => ({
  order_id: order.order_id,
  user_uuid: order.user_uuid,
  entry_kind: 'manual',
  client_order_id: order.client_order_id,
  symbol: order.symbol,
  side: order.side,
  quantity: order.quantity,
  order_type: order.order_type,
  limit_price: order.limit_price,
  status: asBlotterStatus(order.status),
  filled_qty: order.filled_qty,
  avg_fill_price: order.avg_fill_price,
  strategy_position_id: strategyPositionId,
  strategy_name: null,
  strategy_config: null,
  created_at: order.created_at,
  updated_at: order.updated_at,
});

/* ── Inner component that assumes both contexts are available ───────── */

const OrderBlotterInner: React.FC = () => {
  const { userUuid, getToken } = useAuth();
  const {
    subscribeToOrderEvents,
    subscribeToPortfolioStateUpdates,
    subscribeToSystemAlerts,
    subscribeToRiskRejects,
    subscribeToBlotterRowUpserts,
    subscribeToBlotterStatusUpdates,
  } = useOrderTicketNavigation();

  const filledRemovalTimersRef = useRef<Map<string, number>>(new Map());

  const [orders, setOrders] = useState<TradingOrderBlotterRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const safeOrders = useMemo(() => asOrders(orders), [orders]);

  /* ── Snapshot loading ────────────────────────────────────────────── */

  const loadSnapshot = useCallback(async () => {
    if (!userUuid) return;
    try {
      setError(null);
      const token = await getToken();
      const data = await tradingService.listOrderBlotter(userUuid, token || undefined, 1_000);
      setOrders(asOrders(data));
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load order blotter');
    }
  }, [getToken, userUuid]);

  /* ── Order update logic (moved from TradingPage) ─────────────────── */

  const clearFilledRemovalTimer = useCallback((orderId: string) => {
    const existing = filledRemovalTimersRef.current.get(orderId);
    if (existing) {
      window.clearTimeout(existing);
      filledRemovalTimersRef.current.delete(orderId);
    }
  }, []);

  const scheduleFilledRemoval = useCallback(
    (orderId: string) => {
      clearFilledRemovalTimer(orderId);
      const timer = window.setTimeout(() => {
        setOrders((prev) => asOrders(prev).filter((o) => o.order_id !== orderId));
        filledRemovalTimersRef.current.delete(orderId);
      }, 30000);
      filledRemovalTimersRef.current.set(orderId, timer);
    },
    [clearFilledRemovalTimer],
  );

  const applyOrderUpdate = useCallback(
    (incoming: TradingOrder, strategyPositionId: string | null = null) => {
      const blotterRow = asBlotterOrder(incoming, strategyPositionId);
      const isStrategyLinked = Boolean(strategyPositionId);

      if (incoming.status === 'cancelled') {
        clearFilledRemovalTimer(incoming.order_id);
        if (isStrategyLinked) {
          setOrders((prev) => upsertOrder(asOrders(prev), blotterRow));
        } else {
          setOrders((prev) => asOrders(prev).filter((o) => o.order_id !== incoming.order_id));
        }
        return;
      }

      setOrders((prev) => upsertOrder(asOrders(prev), blotterRow));

      if (incoming.status === 'filled') {
        if (isStrategyLinked) {
          clearFilledRemovalTimer(incoming.order_id);
        } else {
          scheduleFilledRemoval(incoming.order_id);
        }
      } else {
        clearFilledRemovalTimer(incoming.order_id);
      }
    },
    [clearFilledRemovalTimer, scheduleFilledRemoval],
  );

  /* ── Cleanup filled-removal timers on unmount ────────────────────── */

  useEffect(() => {
    const timers = filledRemovalTimersRef.current;
    return () => {
      timers.forEach((t) => window.clearTimeout(t));
      timers.clear();
    };
  }, []);

  /* ── Load initial snapshot ───────────────────────────────────────── */

  useEffect(() => {
    if (!userUuid) return;
    void loadSnapshot();
  }, [userUuid, loadSnapshot]);

  /* ── Subscribe to order events ───────────────────────────────────── */

  useEffect(() => {
    return subscribeToOrderEvents((orderEvent) => {
      const extracted = extractOrderFromEvent(orderEvent);
      if (!extracted) return;
      applyOrderUpdate(extracted.order, extracted.strategyPositionId);

      // Safety net: a strategy-child order whose parent strategy row isn't yet
      // in local state would be silently dropped by the group renderer.
      // Reload the snapshot so the parent row is pulled in.
      if (extracted.strategyPositionId) {
        const strategyPositionId = extracted.strategyPositionId;
        setOrders((prev) => {
          const hasParent = asOrders(prev).some(
            (row) =>
              row.entry_kind === 'strategy' &&
              row.strategy_position_id === strategyPositionId,
          );
          if (!hasParent) {
            void loadSnapshot();
          }
          return prev;
        });
      }
    });
  }, [subscribeToOrderEvents, applyOrderUpdate, loadSnapshot]);

  /* ── Subscribe to local blotter row upserts (manual place / strategy schedule) ── */

  useEffect(() => {
    return subscribeToBlotterRowUpserts((row) => {
      setOrders((prev) => upsertOrder(asOrders(prev), row));
    });
  }, [subscribeToBlotterRowUpserts]);

  /* ── Subscribe to blotter status updates (strategy scheduled↔pending) ── */

  useEffect(() => {
    return subscribeToBlotterStatusUpdates((update) => {
      setOrders((prev) =>
        asOrders(prev).map((row) =>
          row.strategy_position_id === update.strategy_position_id
            ? { ...row, status: update.status, updated_at: new Date().toISOString() }
            : row,
        ),
      );
    });
  }, [subscribeToBlotterStatusUpdates]);

  /* ── Subscribe to system alerts ──────────────────────────────────── */

  useEffect(() => {
    return subscribeToSystemAlerts((alert: SystemAlert) => {
      if (isActionableOrderBlotterAlert(alert)) {
        setError(`System alert: ${alert.message}`);
      }
    });
  }, [subscribeToSystemAlerts]);

  /* ── Subscribe to portfolio state updates → reload snapshot ──────── */

  useEffect(() => {
    return subscribeToPortfolioStateUpdates(() => {
      void loadSnapshot();
    });
  }, [subscribeToPortfolioStateUpdates, loadSnapshot]);

  /* ── Subscribe to risk rejects ───────────────────────────────────── */

  useEffect(() => {
    return subscribeToRiskRejects((payload) => {
      setError(`Risk reject: ${JSON.stringify(payload)}`);
    });
  }, [subscribeToRiskRejects]);

  /* ── User actions ────────────────────────────────────────────────── */

  const cancelOrder = useCallback(
    async (orderId: string) => {
      if (!userUuid) return;
      try {
        const token = await getToken();
        const cancelled = await tradingService.cancelOrder(userUuid, orderId, token || undefined);
        applyOrderUpdate(cancelled);
      } catch (err: any) {
        setError(err?.response?.data?.error || err?.message || 'Cancel failed');
      }
    },
    [userUuid, getToken, applyOrderUpdate],
  );

  const cancelStrategy = useCallback(
    async (strategyPositionId: string) => {
      if (!userUuid) return;
      try {
        const token = await getToken();
        await tradingService.closeStrategyPosition(userUuid, strategyPositionId, token || undefined);
        await loadSnapshot();
      } catch (err: any) {
        setError(err?.response?.data?.error || err?.message || 'Failed to cancel scheduled strategy');
      }
    },
    [userUuid, getToken, loadSnapshot],
  );

  /* ── Render ──────────────────────────────────────────────────────── */

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {error && (
        <div
          style={{
            padding: '0.5rem 0.75rem',
            color: 'var(--color-error, #e74c3c)',
            fontSize: '0.85rem',
            borderBottom: '1px solid var(--color-border, #333)',
          }}
        >
          {error}
        </div>
      )}

      <div style={{ flex: 1, overflow: 'auto' }}>
        <OrderBlotter
          orders={safeOrders}
          onCancelOrder={cancelOrder}
          onCancelStrategy={cancelStrategy}
        />
      </div>
    </div>
  );
};

/* ── Error boundary to catch missing context ───────────────────────── */

interface ErrorBoundaryState {
  hasError: boolean;
  message: string;
}

class OrderBlotterErrorBoundary extends React.Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      message: error.message || 'Unexpected error in Order Blotter widget.',
    };
  }

  componentDidCatch(error: Error) {
    // eslint-disable-next-line no-console
    console.error('OrderBlotterWidget render error:', error);
  }

  render() {
    if (this.state.hasError) {
      const msg = this.state.message;
      let displayMessage = msg || 'Unable to render Order Blotter widget.';

      if (msg.includes('OrderTicketNavigationProvider')) {
        displayMessage =
          'OrderTicketNavigationContext is not available. Ensure this widget is rendered inside an OrderTicketNavigationProvider.';
      } else if (msg.includes('AuthProvider')) {
        displayMessage =
          'AuthContext is not available. Ensure this widget is rendered inside an AuthProvider.';
      }

      return (
        <div style={{ padding: '1rem', color: 'var(--color-error, #e74c3c)' }}>
          <strong>Order Blotter unavailable</strong>
          <p style={{ marginTop: '0.5rem' }}>{displayMessage}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Self-contained Order Blotter widget.
 *
 * Loads the initial order blotter snapshot from TradingService, subscribes
 * to order events / portfolio state updates / system alerts / risk rejects
 * via OrderTicketNavigationContext, and manages the full order upsert/remove
 * lifecycle internally (moved from TradingPage).
 */
export const OrderBlotterWidget: React.FC<WidgetInstanceProps> = (_props) => {
  return (
    <OrderBlotterErrorBoundary>
      <OrderBlotterInner />
    </OrderBlotterErrorBoundary>
  );
};

export default OrderBlotterWidget;
